import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Dumbbell, RefreshCw, X, TrendingUp, Info, UtensilsCrossed, BarChart3, Check, Home as HomeIcon, Clipboard, ClipboardCheck, Footprints, Flame, Bike, Waves, PersonStanding, Moon } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import {
  trackStrengthStarted,
  trackStrengthCompleted,
  trackStrengthGoalSelected,
  trackStrengthCtaClicked,
  trackStrengthLadderOpened,
  trackStrengthEquipmentSelected,
  trackStrengthWorkoutCopied,
  trackStrengthRecoveryViewed
} from '../utils/analytics';

// ===== Exercise library with progression ladders =====
// Each exercise has an optional `gym` variant (name, cue, ladder) used when "At the Gym" is selected.
const EXERCISES = {
  squat: { name: 'Bodyweight Squat', unit: 'reps', cue: 'Sit back, knees track over toes, chest tall.', ladder: ['Box / Chair Squat', 'Bodyweight Squat', 'Tempo Squat (3s down)', 'Bulgarian Split Squat', 'Pistol Squat Progression'], gym: { name: 'Goblet Squat', cue: 'Hold a dumbbell at your chest, sit between your hips, drive up through mid-foot.', ladder: ['Goblet Squat', 'Hack Squat', 'Leg Press', 'Barbell Back Squat'] } },
  lunge: { name: 'Reverse Lunge', unit: 'reps', cue: 'Step back, drop straight down, keep front knee stable.', ladder: ['Assisted Lunge (hold support)', 'Static Lunge', 'Reverse Lunge', 'Walking Lunge', 'Deficit Lunge'], gym: { name: 'Dumbbell Walking Lunge', cue: 'Hold dumbbells at your sides; step into a controlled lunge and alternate legs.', ladder: ['Bodyweight Walking Lunge', 'Dumbbell Walking Lunge', 'Dumbbell Reverse Lunge', 'Barbell Lunge'] } },
  gluteBridge: { name: 'Glute Bridge', unit: 'reps', cue: 'Drive through heels, squeeze glutes at the top.', ladder: ['Two-Leg Glute Bridge', 'Marching Glute Bridge', 'Single-Leg Glute Bridge', 'Elevated Single-Leg Bridge', 'Hip Thrust Progression'], gym: { name: 'Hip Thrust', cue: 'Shoulders on a bench, drive hips up, squeeze glutes hard at the top.', ladder: ['Glute Bridge', 'Machine Hip Thrust', 'Barbell Hip Thrust', 'Single-Leg Hip Thrust'] } },
  stepup: { name: 'Step-Up', unit: 'reps', cue: 'Use a sturdy step; push through the top foot.', ladder: ['Low Step-Up', 'Standard Step-Up', 'Tempo Step-Up', 'High Step-Up', 'Step-Up to Knee Drive'], gym: { name: 'Dumbbell Step-Up', cue: 'Hold dumbbells; push through the top foot and control the way down.', ladder: ['Bodyweight Step-Up', 'Dumbbell Step-Up', 'Higher Box Step-Up', 'Step-Up to Knee Drive'] } },
  calfRaise: { name: 'Calf Raise', unit: 'reps', cue: 'Full range, pause at the top.', ladder: ['Two-Leg Calf Raise', 'Tempo Calf Raise', 'Single-Leg Calf Raise', 'Deficit Calf Raise', 'Single-Leg Deficit Raise'], gym: { name: 'Calf Raise (Machine / DB)', cue: 'Full range, pause at the top, control the stretch.', ladder: ['Bodyweight Calf Raise', 'Dumbbell Calf Raise', 'Machine Calf Raise', 'Single-Leg Loaded Raise'] } },
  wallSit: { name: 'Wall Sit', unit: 'sec', cue: 'Thighs parallel, back flat against the wall.', ladder: ['Quarter Wall Sit', 'Half Wall Sit', 'Full Wall Sit', 'Single-Leg Wall Sit', 'Weighted Wall Sit'] },
  pushup: { name: 'Push-Up', unit: 'reps', cue: 'Body in a straight line, elbows ~45°.', ladder: ['Wall Push-Up', 'Incline Push-Up', 'Knee Push-Up', 'Standard Push-Up', 'Decline Push-Up', 'Archer Push-Up'], gym: { name: 'Dumbbell Bench Press', cue: 'Lower the dumbbells to chest level, press up smoothly without slamming the lockout.', ladder: ['Dumbbell Bench Press', 'Barbell Bench Press', 'Incline Bench Press', 'Weighted / Heavier Press'] } },
  pikePushup: { name: 'Pike Push-Up', unit: 'reps', cue: 'Hips high, lower the crown of your head toward the floor.', ladder: ['Downward Dog Hold', 'Pike Push-Up', 'Deficit Pike Push-Up', 'Feet-Elevated Pike', 'Wall Handstand Push-Up'], gym: { name: 'Dumbbell Shoulder Press', cue: 'Press dumbbells overhead, keep ribs down, lower under control.', ladder: ['Seated Dumbbell Press', 'Standing Dumbbell Press', 'Barbell Overhead Press', 'Weighted Press'] } },
  dip: { name: 'Chair Dip', unit: 'reps', cue: 'Keep shoulders down, lower to 90° at the elbow.', ladder: ['Bench Dip (bent knees)', 'Bench Dip (legs straight)', 'Feet-Elevated Dip', 'Parallel Bar Dip', 'Weighted Dip'], gym: { name: 'Tricep Pushdown (Cable)', cue: 'Keep elbows pinned to your sides; full extension at the bottom.', ladder: ['Cable Pushdown', 'Rope Pushdown', 'Overhead DB Extension', 'Close-Grip Press'] } },
  row: { name: 'Towel / Inverted Row', unit: 'reps', cue: 'Squeeze shoulder blades; pull elbows back.', ladder: ['Standing Towel Row', 'Inverted Row (high bar)', 'Inverted Row (lower)', 'Feet-Elevated Inverted Row', 'Tuck Front-Lever Row'], gym: { name: 'Seated Cable Row', cue: 'Pull to your stomach, squeeze your shoulder blades, control the return.', ladder: ['Dumbbell Row', 'Cable Row', 'Chest-Supported Row', 'T-Bar Row'] } },
  superman: { name: 'Superman', unit: 'reps', cue: 'Lift chest and legs, pause, lower with control.', ladder: ['Superman Hold', 'Alternating Superman', 'Superman Pulses', 'W-Raise Superman', 'Weighted Superman'], gym: { name: 'Back Extension', cue: 'Hinge at the hips and extend with your glutes and lower back — no jerking.', ladder: ['Floor Superman', 'Machine Back Extension', 'Weighted Back Extension', 'Single-Leg RDL (DB)'] } },
  plank: { name: 'Plank', unit: 'sec', cue: 'Brace your core, flat back, breathe.', ladder: ['Knee Plank', 'Forearm Plank', 'Plank with Shoulder Taps', 'Long-Lever Plank', 'RKC Plank'], gym: { name: 'Cable Crunch', cue: 'Round through your spine and pull with your abs, not your arms.', ladder: ['Plank', 'Cable Crunch', 'Ab Wheel Rollout', 'Hanging Knee Raise'] } },
  deadbug: { name: 'Dead Bug', unit: 'reps', cue: 'Press low back to the floor; move slowly.', ladder: ['Dead Bug (arms only)', 'Dead Bug (legs only)', 'Full Dead Bug', 'Weighted Dead Bug', 'Hollow Body Hold'] },
  birddog: { name: 'Bird Dog', unit: 'reps', cue: 'Reach opposite arm and leg; keep hips level.', ladder: ['Bird Dog (slow)', 'Bird Dog with Hold', 'Bird Dog Crunch', 'Resisted Bird Dog', 'Plank Bird Dog'] },
  mountainClimber: { name: 'Mountain Climber', unit: 'sec', cue: 'Drive knees in, keep hips low.', ladder: ['Slow Mountain Climber', 'Standard Mountain Climber', 'Cross-Body Climber', 'Spiderman Climber', 'Sprint Climbers'] },
  jumpingJack: { name: 'Jumping Jack', unit: 'sec', cue: 'Stay light on your feet; steady rhythm.', ladder: ['Step Jack (low impact)', 'Standard Jumping Jack', 'Power Jack', 'Plyo Jack', 'Burpee'] }
};

// Resolve the active variant (home vs gym) for an exercise
const resolveExercise = (exKey, equipment) => {
  const ex = EXERCISES[exKey];
  if (equipment === 'gym' && ex.gym) {
    return { name: ex.gym.name, cue: ex.gym.cue, ladder: ex.gym.ladder, unit: ex.unit };
  }
  return { name: ex.name, cue: ex.cue, ladder: ex.ladder, unit: ex.unit };
};

// ===== Training splits by days/week =====
const SPLITS = {
  2: [
    { name: 'Full Body A', focus: 'Full Body', keys: ['squat', 'pushup', 'row', 'plank', 'jumpingJack'] },
    { name: 'Full Body B', focus: 'Full Body', keys: ['lunge', 'pikePushup', 'superman', 'deadbug', 'mountainClimber'] }
  ],
  3: [
    { name: 'Full Body A', focus: 'Full Body', keys: ['squat', 'pushup', 'row', 'plank'] },
    { name: 'Full Body B', focus: 'Full Body', keys: ['lunge', 'dip', 'superman', 'deadbug'] },
    { name: 'Full Body C', focus: 'Full Body', keys: ['gluteBridge', 'pikePushup', 'row', 'birddog'] }
  ],
  4: [
    { name: 'Upper A', focus: 'Upper Body', keys: ['pushup', 'row', 'dip', 'plank'] },
    { name: 'Lower A', focus: 'Lower Body', keys: ['squat', 'lunge', 'gluteBridge', 'calfRaise'] },
    { name: 'Upper B', focus: 'Upper Body', keys: ['pikePushup', 'superman', 'dip', 'deadbug'] },
    { name: 'Lower B', focus: 'Lower Body', keys: ['stepup', 'gluteBridge', 'wallSit', 'birddog'] }
  ],
  5: [
    { name: 'Lower A', focus: 'Lower Body', keys: ['squat', 'lunge', 'gluteBridge', 'calfRaise'] },
    { name: 'Push', focus: 'Push', keys: ['pushup', 'pikePushup', 'dip', 'plank'] },
    { name: 'Pull & Core', focus: 'Pull & Core', keys: ['row', 'superman', 'birddog', 'deadbug'] },
    { name: 'Lower B', focus: 'Lower Body', keys: ['stepup', 'gluteBridge', 'wallSit', 'calfRaise'] },
    { name: 'Full Body', focus: 'Full Body', keys: ['squat', 'pushup', 'row', 'mountainClimber', 'jumpingJack'] }
  ]
};

// Goal → rep prescription
const REP_RANGES = {
  fat_loss: { reps: [15, 20], sec: [30, 45], rest: '30–45s' },
  muscle: { reps: [8, 12], sec: [25, 40], rest: '60–90s' },
  strength: { reps: [6, 10], sec: [20, 35], rest: '75–90s' },
  general: { reps: [10, 15], sec: [25, 40], rest: '45–60s' }
};

const GOALS = [
  { value: 'fat_loss', label: 'Fat Loss', desc: 'Higher reps & short rest to support calorie burn' },
  { value: 'muscle', label: 'Muscle Preservation', desc: 'Moderate reps to help protect lean mass' },
  { value: 'strength', label: 'Build Strength', desc: 'Lower reps with progressive overload' },
  { value: 'general', label: 'General Fitness', desc: 'Balanced training to move and feel better' }
];

const LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const DAYS_OPTIONS = [2, 3, 4, 5];

const LIMITATIONS = [
  { value: 'knees', label: 'Knees' },
  { value: 'lower_back', label: 'Lower Back' },
  { value: 'shoulders', label: 'Shoulders' },
  { value: 'wrists', label: 'Wrists' }
];

// Sets by experience level
const setsForLevel = (level) => (level === 'beginner' ? 2 : level === 'advanced' ? 4 : 3);

// Build a personalized prescription string for one exercise
const buildPrescription = (exKey, level, goal, age, equipment) => {
  const ex = EXERCISES[exKey];
  const variant = resolveExercise(exKey, equipment);
  const range = REP_RANGES[goal] || REP_RANGES.general;
  const sets = setsForLevel(level);
  const isTimed = ex.unit === 'sec';
  let [lo, hi] = isTimed ? range.sec : range.reps;
  // Older trainees: trim the top of the range slightly for recovery
  if (Number(age) >= 50) hi = Math.max(lo, hi - (isTimed ? 5 : 2));
  const target = isTimed ? `${lo}–${hi} sec` : `${lo}–${hi} reps`;
  const next = variant.ladder[1] || variant.ladder[0];
  return { name: variant.name, sets, target, rest: range.rest, cue: variant.cue, key: exKey, next };
};

// Recovery & Cardio suggestions (compliance-safe)
const RECOVERY_ITEMS = [
  { id: 'walking', icon: 'Footprints', color: '#22C55E', name: 'Walking', suggestion: '20–30 min on rest days or after workouts. One of the most underrated tools for fat loss support and recovery.', note: 'May support calorie balance without adding recovery stress.' },
  { id: 'sauna', icon: 'Flame', color: '#F59E0B', name: 'Sauna', suggestion: '15–20 min post-workout or on rest days. Commonly used for relaxation and circulation.', note: 'Stay well hydrated, especially on GLP-1 medications which can reduce thirst signals.' },
  { id: 'cycling', icon: 'Bike', color: '#3B82F6', name: 'Low-Intensity Cycling', suggestion: '20–40 min at a pace where you can hold a conversation. Stationary or outdoor both work.', note: 'A good option for days when joints feel heavy or motivation is lower.' },
  { id: 'swimming', icon: 'Waves', color: '#06B6D4', name: 'Swimming / Water Walking', suggestion: 'Excellent low-impact option for anyone with knee, hip, or back limitations.', note: 'May help support mobility and cardiovascular health without joint stress.' },
  { id: 'mobility', icon: 'PersonStanding', color: '#A855F7', name: 'Stretching / Mobility', suggestion: '10–15 min daily. Focus on hips, chest, and shoulders — areas that tighten from daily life and GLP-1 fatigue.', note: 'Consistent mobility work can help with soreness and long-term consistency.' },
  { id: 'sleep', icon: 'Moon', color: '#818CF8', name: 'Sleep', suggestion: '7–9 hours. The most important recovery tool — especially while in a calorie deficit.', note: 'Sleep quality affects hunger hormones, muscle repair, and energy. Prioritize it.' }
];

const RECOVERY_ICONS = { Footprints, Flame, Bike, Waves, PersonStanding, Moon };

const EQUIPMENT_OPTIONS = [
  { value: 'home', label: 'At Home', sub: 'No equipment needed' },
  { value: 'gym', label: 'At the Gym', sub: 'Dumbbells, cables, machines' }
];

const WARMUP_TEXT = '5 min easy movement (march in place, arm circles, bodyweight squats) + 1 light set of your first exercise.';
const COOLDOWN_TEXT = '5 min easy walking + light stretching for the muscles you trained.';

const buildLimitationsNote = (limits) => {
  if (!limits.length) return null;
  const tips = [];
  if (limits.includes('knees')) tips.push('For knee-friendly training, reduce range on squats/lunges and swap jumping movements for low-impact step versions.');
  if (limits.includes('lower_back')) tips.push('To protect your lower back, brace your core on every rep and favor the easier end of each progression ladder.');
  if (limits.includes('shoulders')) tips.push('For shoulder comfort, start push-ups on an incline and keep elbows tucked closer to your sides.');
  if (limits.includes('wrists')) tips.push('If wrists are sensitive, perform push movements on fists or push-up handles to keep wrists neutral.');
  return tips;
};

const StrengthPlan = () => {
  const navigate = useNavigate();
  const startedRef = useRef(false);

  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [goal, setGoal] = useState('');
  const [level, setLevel] = useState('');
  const [days, setDays] = useState('');
  const [limitations, setLimitations] = useState([]);
  const [equipment, setEquipment] = useState('home');

  const [plan, setPlan] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [ladder, setLadder] = useState(null); // { name, steps }
  const [copiedDay, setCopiedDay] = useState(null);

  const recoveryRef = useRef(null);
  const recoveryFiredRef = useRef(false);

  // Fire recovery-section-viewed once when it scrolls into view
  useEffect(() => {
    if (!plan || !recoveryRef.current || recoveryFiredRef.current) return undefined;
    const el = recoveryRef.current;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !recoveryFiredRef.current) {
          recoveryFiredRef.current = true;
          trackStrengthRecoveryViewed({ goal: plan.goal, level: plan.level });
          obs.disconnect();
        }
      });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [plan]);

  const fireStartedOnce = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackStrengthStarted({ goal: goal || 'not_set', level: level || 'not_set', days: days || 'not_set' });
    }
  };

  const selectEquipment = (value) => {
    fireStartedOnce();
    setEquipment(value);
    trackStrengthEquipmentSelected(value, { goal: goal || 'not_set', level: level || 'not_set' });
  };

  const toggleLimitation = (value) => {
    fireStartedOnce();
    setLimitations((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const selectGoal = (value) => {
    fireStartedOnce();
    setGoal(value);
    trackStrengthGoalSelected(value, { level: level || 'not_set', days: days || 'not_set' });
  };

  const isComplete = age && weight && goal && level && days;

  const generatePlan = () => {
    const split = SPLITS[Number(days)] || SPLITS[3];
    const week1 = split.map((d) => ({
      name: d.name,
      focus: d.focus,
      exercises: d.keys.map((k) => buildPrescription(k, level, goal, age, equipment))
    }));

    const w = Number(weight) || 175;
    const proteinLow = Math.round(w * 0.7);
    const proteinHigh = Math.round(w * 1.0);

    const conditioning = goal === 'fat_loss'
      ? '2–3 rounds finisher: 30s jumping jacks → 30s mountain climbers → 30s march in place, resting as needed.'
      : null;

    const goalLabel = (GOALS.find((g) => g.value === goal) || {}).label || 'your goal';

    const newPlan = {
      goal, goalLabel, level, days: Number(days), age: Number(age), weight: w, equipment,
      week1,
      proteinLow,
      proteinHigh,
      conditioning,
      limitationsNote: buildLimitationsNote(limitations),
      phases: [
        { weeks: 'Weeks 1–4', title: 'Foundation', desc: `Learn the movements and build a consistent ${days}-day rhythm. Stick to the first or second step of each progression ladder and focus on clean reps.` },
        { weeks: 'Weeks 5–8', title: 'Build', desc: `Add a set where it feels manageable and push toward the top of each rep range. Advance one ladder step on movements that feel easy — supporting your ${goalLabel.toLowerCase()} goal.` },
        { weeks: 'Weeks 9–12', title: 'Progress', desc: 'Move to harder ladder steps, add tempo (slow negatives), and tighten rest periods. Re-run this builder anytime your inputs change.' }
      ]
    };

    setPlan(newPlan);
    setActiveDay(0);
    trackStrengthCompleted({ goal, level, days: Number(days), age: Number(age), weight: w, equipment });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const rebuild = () => {
    setPlan(null);
    setActiveDay(0);
    setCopiedDay(null);
    startedRef.current = false;
    recoveryFiredRef.current = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openLadder = (exKey) => {
    const variant = resolveExercise(exKey, plan ? plan.equipment : equipment);
    setLadder({ name: variant.name, steps: variant.ladder });
    trackStrengthLadderOpened(variant.name, { goal, level, days: Number(days), equipment: plan ? plan.equipment : equipment });
  };

  const copyWorkout = async (dayIndex) => {
    const d = plan.week1[dayIndex];
    const equipLabel = plan.equipment === 'gym' ? 'Gym' : 'Home';
    const lines = [];
    lines.push(`${d.name} — Week 1`);
    lines.push(`Goal: ${plan.goalLabel} | Level: ${plan.level} | Equipment: ${equipLabel}`);
    lines.push('');
    d.exercises.forEach((ex, i) => {
      lines.push(`${i + 1}. ${ex.name}`);
      lines.push(`   Sets/Reps: ${ex.sets} sets × ${ex.target}`);
      lines.push(`   Rest: ${ex.rest}`);
      lines.push(`   Cue: ${ex.cue}`);
      lines.push(`   Next progression: ${ex.next}`);
      lines.push('');
    });
    if (plan.conditioning) {
      lines.push(`Finisher: ${plan.conditioning}`);
      lines.push('');
    }
    lines.push(`Warm-up: ${WARMUP_TEXT}`);
    lines.push(`Cool-down: ${COOLDOWN_TEXT}`);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedDay(dayIndex);
    trackStrengthWorkoutCopied(d.name, plan.equipment, { goal: plan.goal, level: plan.level });
    setTimeout(() => setCopiedDay((cur) => (cur === dayIndex ? null : cur)), 2000);
  };

  const handleCta = (button, path) => {
    trackStrengthCtaClicked(button, { goal, level, days: Number(days) });
    navigate(path);
  };

  // ===== RESULTS VIEW =====
  if (plan) {
    const day = plan.week1[activeDay];
    return (
      <div className="min-h-screen bg-[#0B1020] text-white pb-32">
        <SEOHead
          title="Your GLP-1 Strength Plan | Weight Loss Care Team"
          description="Your personalized 12-week bodyweight strength plan designed to help protect muscle and support consistency."
          path="/strength-plan"
        />

        {/* Header */}
        <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Dumbbell className="w-7 h-7 text-[#A855F7]" />
              <h1 className="text-2xl sm:text-3xl font-bold">Your 12-Week Strength Plan</h1>
            </div>
            <p className="text-[#94A3B8]">
              Built for <span className="text-[#F8FAFC] font-medium">{plan.goalLabel}</span> · {plan.level} · {plan.days} days/week · {plan.equipment === 'gym' ? 'Gym' : 'Home'}
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 -mt-4 space-y-6">
          {/* Rebuild */}
          <Button
            onClick={rebuild}
            variant="outline"
            className="border-[#334155] text-white hover:bg-[#1E293B]"
            data-testid="rebuild-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rebuild Plan
          </Button>

          {/* 12-Week Overview */}
          <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="program-overview">
            <h2 className="text-lg font-bold text-[#F8FAFC] mb-4">12-Week Program Overview</h2>
            <div className="space-y-3">
              {plan.phases.map((p) => (
                <div key={p.title} className="bg-[#0B1020] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-[#A855F7] bg-[#A855F7]/15 px-2 py-0.5 rounded">{p.weeks}</span>
                    <span className="font-semibold text-[#F8FAFC]">{p.title}</span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{p.desc}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Weekly Split */}
          <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="weekly-split">
            <h2 className="text-lg font-bold text-[#F8FAFC] mb-4">Weekly Training Split</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {plan.week1.map((d, i) => (
                <div key={d.name} className="flex items-center gap-3 bg-[#0B1020] rounded-lg p-3">
                  <div className="w-8 h-8 rounded-full bg-[#A855F7]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#A855F7] text-sm font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#F8FAFC]">{d.name}</p>
                    <p className="text-xs text-[#94A3B8]">{d.focus} · {d.exercises.length} exercises</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#64748B] mt-3">Spread your {plan.days} sessions across the week with at least one rest day between hard days.</p>
          </Card>

          {/* Week 1 Sample Workouts */}
          <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="week1-workouts">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h2 className="text-lg font-bold text-[#F8FAFC]">Week 1 Sample Workouts</h2>
              <button
                onClick={() => copyWorkout(activeDay)}
                className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                  copiedDay === activeDay
                    ? 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/10'
                    : 'border-[#334155] text-[#94A3B8] hover:text-white hover:bg-[#0B1020]'
                }`}
                data-testid="copy-workout-btn"
              >
                {copiedDay === activeDay ? <ClipboardCheck className="w-4 h-4" /> : <Clipboard className="w-4 h-4" />}
                {copiedDay === activeDay ? 'Copied!' : 'Copy Workout'}
              </button>
            </div>

            {/* Day tabs (horizontal scroll on mobile) */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-4" data-testid="day-tabs">
              {plan.week1.map((d, i) => (
                <button
                  key={d.name}
                  onClick={() => setActiveDay(i)}
                  className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                    activeDay === i ? 'bg-[#A855F7] text-white' : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`day-tab-${i}`}
                >
                  {d.name}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {day.exercises.map((ex) => (
                <div key={ex.key} className="bg-[#0B1020] rounded-lg p-4" data-testid={`exercise-${ex.key}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#F8FAFC]">{ex.name}</p>
                      <p className="text-sm text-[#A855F7] mt-0.5">{ex.sets} sets × {ex.target} · rest {ex.rest}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{ex.cue}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => openLadder(ex.key)}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#3B82F6] hover:text-[#60A5FA] font-medium"
                    data-testid={`ladder-btn-${ex.key}`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    View progression ladder
                  </button>
                </div>
              ))}
            </div>

            {plan.conditioning && (
              <div className="mt-4 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-lg p-4">
                <p className="text-sm font-semibold text-[#22C55E] mb-1">Optional Conditioning Finisher</p>
                <p className="text-sm text-[#CBD5E1]">{plan.conditioning}</p>
              </div>
            )}

            {plan.equipment === 'gym' && (
              <p className="text-xs text-[#64748B] mt-4 italic" data-testid="gym-note">
                Work with a trainer or gym staff if you're new to any of these movements. Form matters more than load.
              </p>
            )}
          </Card>

          {/* Limitations note */}
          {plan.limitationsNote && plan.limitationsNote.length > 0 && (
            <Card className="bg-[#F59E0B]/10 border-[#F59E0B]/30 p-5" data-testid="limitations-note">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#F8FAFC] mb-2">Adjusting for your limitations</p>
                  <ul className="space-y-1.5">
                    {plan.limitationsNote.map((t) => (
                      <li key={t} className="text-sm text-[#CBD5E1]">• {t}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          )}

          {/* GLP-1 Training Note (compliant) */}
          <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="glp1-note">
            <h2 className="text-lg font-bold text-[#F8FAFC] mb-2">Training While on a GLP-1 Program</h2>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              Resistance training is commonly used to help support muscle while losing weight. Because GLP-1 programs
              often reduce appetite and overall intake, pairing them with strength work and adequate protein may help
              protect lean mass and can help with consistency. This plan is designed to help you build a sustainable
              routine — results vary from person to person.
            </p>
          </Card>

          {/* Nutrition Recommendation (compliant) */}
          <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="nutrition-note">
            <h2 className="text-lg font-bold text-[#F8FAFC] mb-2">Protein &amp; Nutrition</h2>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              Protein is one of the most important factors for preserving muscle during weight loss. A common general
              guideline is roughly <span className="text-[#22C55E] font-semibold">{plan.proteinLow}–{plan.proteinHigh}g of protein per day</span> for
              someone at your listed weight. Spreading protein across your meals may help support recovery and
              consistency. Always follow your provider's guidance — individual needs vary.
            </p>
          </Card>

          {/* Recovery & Cardio */}
          <Card ref={recoveryRef} className="bg-[#1E293B] border-[#334155] p-6" data-testid="recovery-section">
            <h2 className="text-lg font-bold text-[#F8FAFC] mb-1">Recovery &amp; Cardio</h2>
            <p className="text-sm text-[#94A3B8] mb-5">
              Low-impact options that pair well with strength training — especially on GLP-1 programs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {RECOVERY_ITEMS.map((item) => {
                const IconCmp = RECOVERY_ICONS[item.icon];
                return (
                  <div key={item.id} className="bg-[#0B1020] rounded-lg p-4" data-testid={`recovery-${item.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}26` }}>
                        <IconCmp className="w-5 h-5" style={{ color: item.color }} />
                      </div>
                      <p className="font-semibold text-[#F8FAFC]">{item.name}</p>
                    </div>
                    <p className="text-sm text-[#CBD5E1] mb-2">{item.suggestion}</p>
                    <p className="text-xs text-[#94A3B8] italic">{item.note}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#64748B] mt-5">
              These are general wellness suggestions, not medical guidance. Follow your provider's recommendations.
            </p>
          </Card>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-[#A855F7]/10 to-[#3B82F6]/10 border-[#A855F7]/30 p-6 sm:p-8" data-testid="strength-cta">
            <h2 className="text-xl font-bold text-[#F8FAFC] mb-2">Want to pair this with your nutrition plan?</h2>
            <p className="text-[#94A3B8] mb-6">
              This plan gives you the training structure. Your meal plan gives you the protein and grocery roadmap.
              Together, they help support fat loss, muscle preservation, and consistency.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => handleCta('meal_plan', '/meal-plan')}
                className="w-full sm:flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white py-6 text-base font-semibold rounded-xl"
                data-testid="cta-meal-plan"
              >
                <UtensilsCrossed className="w-5 h-5 mr-2" />
                Build My Meal Plan
              </Button>
              <Button
                onClick={() => handleCta('forecast', '/calculator')}
                className="w-full sm:flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white py-6 text-base font-semibold rounded-xl"
                data-testid="cta-forecast"
              >
                <BarChart3 className="w-5 h-5 mr-2" />
                Start 90-Day Forecast
              </Button>
            </div>
          </Card>

          {/* Disclaimer */}
          <p className="text-xs text-[#64748B] leading-relaxed text-center px-2" data-testid="strength-disclaimer">
            This tool is for general wellness and educational purposes only. It is not medical advice. Always follow
            your provider's guidance and modify exercise as needed based on your health, injuries, and fitness level.
          </p>
        </div>

        {/* Progression Ladder Modal */}
        {ladder && (
          <div
            className="fixed inset-0 z-[10000] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setLadder(null)}
            data-testid="ladder-modal"
          >
            <div
              className="bg-[#1E293B] border border-[#334155] w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[#1E293B] flex items-center justify-between p-5 border-b border-[#334155]">
                <div>
                  <p className="text-xs text-[#A855F7] font-semibold uppercase tracking-wide">Progression Ladder</p>
                  <h3 className="text-lg font-bold text-[#F8FAFC]">{ladder.name}</h3>
                </div>
                <button
                  onClick={() => setLadder(null)}
                  className="w-10 h-10 rounded-full bg-[#0B1020] flex items-center justify-center text-[#94A3B8] hover:text-white flex-shrink-0"
                  data-testid="ladder-close"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5">
                <p className="text-sm text-[#94A3B8] mb-4">Work from the top down. Move to the next step only when you can complete all your sets with clean form.</p>
                <ol className="space-y-2">
                  {ladder.steps.map((step, i) => (
                    <li key={step} className="flex items-center gap-3 bg-[#0B1020] rounded-lg p-3">
                      <span className="w-7 h-7 rounded-full bg-[#A855F7]/20 text-[#A855F7] text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <span className="text-sm text-[#E2E8F0]">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== FORM VIEW =====
  return (
    <div className="min-h-screen bg-[#0B1020] text-white pb-32">
      <SEOHead
        title="Free GLP-1 Strength Plan Builder | 12-Week Bodyweight Workout Plan"
        description="Build a free 12-week bodyweight strength plan designed for GLP-1 users and weight loss patients who want to support muscle, energy, and consistency."
        path="/strength-plan"
      />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/" className="text-[#94A3B8] hover:text-white text-sm mb-4 inline-block">← Back to Home</Link>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Dumbbell className="w-8 h-8 text-[#A855F7]" />
            <h1 className="text-2xl sm:text-3xl font-bold">GLP-1 Strength Plan Builder</h1>
          </div>
          <p className="text-[#94A3B8] text-base sm:text-lg">
            Build a simple 12-week bodyweight plan designed to help protect muscle, support fat loss, and improve
            consistency while losing weight.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        <Card className="bg-[#1E293B] border-[#334155] p-6">
          {/* Age + Weight */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[#94A3B8] mb-2">Age</label>
              <input
                type="number"
                value={age}
                onChange={(e) => { fireStartedOnce(); setAge(e.target.value); }}
                placeholder="e.g., 42"
                className="w-full bg-[#0B1020] border border-[#334155] rounded-lg px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#A855F7]"
                data-testid="input-age"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#94A3B8] mb-2">Weight (lbs)</label>
              <input
                type="number"
                value={weight}
                onChange={(e) => { fireStartedOnce(); setWeight(e.target.value); }}
                placeholder="e.g., 190"
                className="w-full bg-[#0B1020] border border-[#334155] rounded-lg px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#A855F7]"
                data-testid="input-weight"
              />
            </div>
          </div>

          {/* Primary Goal */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Primary Goal</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {GOALS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => selectGoal(opt.value)}
                  className={`text-left p-4 rounded-lg transition-all border ${
                    goal === opt.value
                      ? 'bg-[#A855F7]/15 border-[#A855F7]'
                      : 'bg-[#0B1020] border-transparent hover:bg-[#334155]'
                  }`}
                  data-testid={`goal-${opt.value}`}
                >
                  <p className="text-sm font-semibold text-[#F8FAFC]">{opt.label}</p>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Experience Level */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Experience Level</label>
            <div className="grid grid-cols-3 gap-2">
              {LEVELS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { fireStartedOnce(); setLevel(opt.value); }}
                  className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${
                    level === opt.value ? 'bg-[#A855F7] text-white' : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`level-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Days Per Week */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Days Per Week</label>
            <div className="grid grid-cols-4 gap-2">
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => { fireStartedOnce(); setDays(String(d)); }}
                  className={`py-3 rounded-lg text-sm font-medium transition-all ${
                    days === String(d) ? 'bg-[#A855F7] text-white' : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`days-${d}`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>

          {/* Injuries / Limitations (optional) */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Injuries / Limitations <span className="text-[#64748B]">(optional)</span></label>
            <div className="flex flex-wrap gap-2">
              {LIMITATIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleLimitation(opt.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                    limitations.includes(opt.value)
                      ? 'bg-[#F59E0B]/15 border-[#F59E0B] text-[#F8FAFC]'
                      : 'bg-[#0B1020] border-transparent text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`limitation-${opt.value}`}
                >
                  {limitations.includes(opt.value) && <Check className="w-3.5 h-3.5 inline mr-1.5 text-[#F59E0B]" />}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Where will you train? (equipment) */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Where will you train?</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {EQUIPMENT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => selectEquipment(opt.value)}
                  className={`flex items-center gap-3 text-left p-4 rounded-lg transition-all border ${
                    equipment === opt.value
                      ? 'bg-[#A855F7]/15 border-[#A855F7]'
                      : 'bg-[#0B1020] border-transparent hover:bg-[#334155]'
                  }`}
                  data-testid={`equipment-${opt.value}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-[#1E293B] flex items-center justify-center flex-shrink-0">
                    {opt.value === 'home' ? <HomeIcon className="w-5 h-5 text-[#A855F7]" /> : <Dumbbell className="w-5 h-5 text-[#A855F7]" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#F8FAFC]">{opt.label}</p>
                    <p className="text-xs text-[#94A3B8] mt-0.5">{opt.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Generate */}
          <Button
            onClick={generatePlan}
            disabled={!isComplete}
            className={`w-full py-6 text-lg font-semibold rounded-xl transition-all ${
              isComplete ? 'bg-[#A855F7] hover:bg-[#9333EA] text-white' : 'bg-[#334155] text-[#64748B] cursor-not-allowed'
            }`}
            data-testid="generate-strength-btn"
          >
            Build My Strength Plan
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-xs text-[#64748B] text-center mt-4">
            Free · No login or email required · Generated instantly on your device
          </p>
        </Card>
      </div>
    </div>
  );
};

export default StrengthPlan;
